import { getMappedSymbol as getUnifiedMappedSymbol } from './unifiedSymbolMapping';
import { getFieldValue } from './dataModeUtils';
import type { DataMode } from '../types/DataMode';

export interface OptionType {
  symbol: string;
  strike: number;
  expiry: string;
  type: 'C' | 'P'; // Call or Put
  fullSymbol: string;
}

export interface OptionsScannerData {
  Symbol: string;
  Strike: number;
  'Expiry Date': string;
  'Bid Count': number;
  'Ask Count': number;
  'Trade Count': number;
  'Bid Volume': number;
  'Ask Volume': number;
  'Delta ( ask - bid )': number;
  Direction: string;
  Sentiment: string;
  'Bid Count accumelated': number;
  'Ask Count accumelated': number;
  'Trades accumelated': number;
  'Bid Volume accumelated': number;
  'Ask Volume accumelated': number;
  'Delta accumelated': number;
  'Direction accumelated': string;
  'Sentiment accumelated': string;
  Last: number;
  'MMEC Gap': number;
  'Vol/OI': number;
  'Open Interest': number;
  Volume: number;
  Haltz: number;
  Band: number;
  'Haltz Rank': number;
  Zahn: number;
  'Zahn Rank': number;
  // New fields for options analysis
  optionType: 'C' | 'P';
  underlyingSymbol: string;
  properSentiment: string;
  properDirection: string;
  optionsAwareHaltz: number;
  optionsAwareBand: number;
  optionsAwareZahn: number;
  optionsAwareMal: number;
  underlyingSentiment: string;
  optionStrategy: string;
  // OTM/ATM/ITM classification
  moneyness: 'OTM' | 'ATM' | 'ITM' | 'Unknown';
  // Numeric moneyness for filtering
  numericMoneyness: number;
  // Greeks
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  // New option volume analysis fields
  boughtCalls: number;
  soldCalls: number;
  boughtPuts: number;
  soldPuts: number;
  totalVolume: number;
  netVolume: number;
  volumeFlow: number;
  volumePressure: number;
  directionalPressure: number;
  fearIndex: number;
  greedIndex: number;
  callFlow: number;
  putFlow: number;
  overallFlow: number;
}

/**
 * Extract option type and details from symbol
 * Expected format: "NVDA 20250813C100" or "NVDA 20250813P100"
 */
export function extractOptionType(symbol: string): OptionType | null {
  try {
    // Handle null/undefined/empty values
    if (!symbol || typeof symbol !== 'string') {
      return null;
    }
    
    // Remove any extra spaces and clean the symbol
    const cleanSymbol = symbol.trim();
    
    if (!cleanSymbol) {
      return null;
    }
    
    // Multiple patterns to match different option formats
    // Pattern 1: SYMBOL YYYYMMDD[C/P]STRIKE (e.g., "NVDA 20250813C100")
    let pattern = /^([A-Z]+)\s+(\d{8})([CP])(\d+(?:\.\d+)?)$/;
    let match = cleanSymbol.match(pattern);
    
    if (match) {
      const [, underlying, expiry, type, strike] = match;
      
      // Format expiry date (YYYYMMDD -> YYYY-MM-DD)
      const formattedExpiry = `${expiry.slice(0, 4)}-${expiry.slice(4, 6)}-${expiry.slice(6, 8)}`;
      
      return {
        symbol: underlying,
        strike: parseFloat(strike),
        expiry: formattedExpiry,
        type: type as 'C' | 'P',
        fullSymbol: cleanSymbol
      };
    }
    
    // Pattern 2: SYMBOL YYMMDD[C/P]STRIKE (e.g., "SPXW 250707C6140")
    pattern = /^([A-Z]+)\s+(\d{6})([CP])(\d+(?:\.\d+)?)$/;
    match = cleanSymbol.match(pattern);
    
    if (match) {
      const [, underlying, expiry, type, strike] = match;
      
      // Format expiry date (YYMMDD -> 20YY-MM-DD)
      const year = `20${expiry.slice(0, 2)}`;
      const month = expiry.slice(2, 4);
      const day = expiry.slice(4, 6);
      const formattedExpiry = `${year}-${month}-${day}`;
      
      return {
        symbol: underlying,
        strike: parseFloat(strike),
        expiry: formattedExpiry,
        type: type as 'C' | 'P',
        fullSymbol: cleanSymbol
      };
    }
    
    // Pattern 3: SYMBOL YYYY-MM-DD[C/P]STRIKE (e.g., "NVDA 2025-08-13C100")
    pattern = /^([A-Z]+)\s+(\d{4})-(\d{2})-(\d{2})([CP])(\d+(?:\.\d+)?)$/;
    match = cleanSymbol.match(pattern);
    
    if (match) {
      const [, underlying, year, month, day, type, strike] = match;
      
      return {
        symbol: underlying,
        strike: parseFloat(strike),
        expiry: `${year}-${month}-${day}`,
        type: type as 'C' | 'P',
        fullSymbol: cleanSymbol
      };
    }
    
    // Pattern 4: SYMBOL YYYYMMDD[C/P]STRIKE (no space, e.g., "NVDA20250813C100")
    pattern = /^([A-Z]+)(\d{8})([CP])(\d+(?:\.\d+)?)$/;
    match = cleanSymbol.match(pattern);
    
    if (match) {
      const [, underlying, expiry, type, strike] = match;
      
      // Format expiry date (YYYYMMDD -> YYYY-MM-DD)
      const formattedExpiry = `${expiry.slice(0, 4)}-${expiry.slice(4, 6)}-${expiry.slice(6, 8)}`;
      
      return {
        symbol: underlying,
        strike: parseFloat(strike),
        expiry: formattedExpiry,
        type: type as 'C' | 'P',
        fullSymbol: cleanSymbol
      };
    }
    
    // Pattern 5: SYMBOL YYMMDD[C/P]STRIKE (no space, e.g., "SPXW250707C6140")
    pattern = /^([A-Z]+)(\d{6})([CP])(\d+(?:\.\d+)?)$/;
    match = cleanSymbol.match(pattern);
    
    if (match) {
      const [, underlying, expiry, type, strike] = match;
      
      // Format expiry date (YYMMDD -> 20YY-MM-DD)
      const year = `20${expiry.slice(0, 2)}`;
      const month = expiry.slice(2, 4);
      const day = expiry.slice(4, 6);
      const formattedExpiry = `${year}-${month}-${day}`;
      
      return {
        symbol: underlying,
        strike: parseFloat(strike),
        expiry: formattedExpiry,
        type: type as 'C' | 'P',
        fullSymbol: cleanSymbol
      };
    }
    
    // Pattern 6: Look for C or P in the symbol and extract basic info
    if (cleanSymbol.includes('C') || cleanSymbol.includes('P')) {
      const cIndex = cleanSymbol.indexOf('C');
      const pIndex = cleanSymbol.indexOf('P');
      const optionIndex = cIndex > -1 ? cIndex : pIndex;
      const optionType = cIndex > -1 ? 'C' : 'P';
      
      if (optionIndex > 0) {
        // Extract underlying (everything before the option part)
        const underlying = cleanSymbol.substring(0, optionIndex).trim();
        
        // Extract the rest after C/P
        const optionPart = cleanSymbol.substring(optionIndex + 1);
        
        // Try to extract strike price (numbers after C/P)
        const strikeMatch = optionPart.match(/^(\d+(?:\.\d+)?)/);
        const strike = strikeMatch ? parseFloat(strikeMatch[1]) : 0;
        
        return {
          symbol: underlying,
          strike: strike,
          expiry: 'Unknown', // Can't extract from this format
          type: optionType as 'C' | 'P',
          fullSymbol: cleanSymbol
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting option type:', error);
    return null;
  }
}

/**
 * Calculate proper sentiment for options based on contract type
 * For calls: bullish sentiment means positive delta (more calls being bought)
 * For puts: bullish sentiment means negative delta (more puts being bought)
 */
export function calculateOptionsSentiment(
  delta: number, 
  optionType: 'C' | 'P', 
  bidVolume: number, 
  askVolume: number
): { sentiment: string; direction: string; strength: number } {
  try {
    let sentiment: string;
    let direction: string;
    let strength: number;
    
    // CORRECT: Calculate volume ratio for sentiment
    // bidVolume = selling, askVolume = buying
    const totalVolume = bidVolume + askVolume;
    const volumeRatio = totalVolume > 0 ? (askVolume - bidVolume) / totalVolume : 0;
    
    if (optionType === 'C') {
      // CORRECT: For calls: bought calls (ask > bid) = bullish, sold calls (bid > ask) = bearish
      if (volumeRatio > 0.1) {
        sentiment = 'Bullish';
        direction = 'Up';
        strength = Math.abs(volumeRatio);
      } else if (volumeRatio < -0.1) {
        sentiment = 'Bearish';
        direction = 'Down';
        strength = Math.abs(volumeRatio);
      } else {
        sentiment = 'Neutral';
        direction = 'Sideways';
        strength = 0;
      }
    } else {
      // CORRECT: For puts: bought puts (ask > bid) = bearish, sold puts (bid > ask) = bullish
      if (volumeRatio > 0.1) {
        sentiment = 'Bearish';
        direction = 'Down';
        strength = Math.abs(volumeRatio);
      } else if (volumeRatio < -0.1) {
        sentiment = 'Bullish';
        direction = 'Up';
        strength = Math.abs(volumeRatio);
      } else {
        sentiment = 'Neutral';
        direction = 'Sideways';
        strength = 0;
      }
    }
    
    return { sentiment, direction, strength };
  } catch (error) {
    console.error('Error calculating options sentiment:', error);
    return { sentiment: 'Neutral', direction: 'Sideways', strength: 0 };
  }
}

/**
 * Calculate options-aware Haltz value with IV
 * Haltz = (Bid Volume - Ask Volume) * IV / (Bid Volume + Ask Volume)
 * For options: Adjust based on option type and sentiment
 */
export function calculateOptionsAwareHaltz(
  bidVolume: number,
  askVolume: number,
  optionType: "C" | "P",
  _delta: number,
  iv: number = 0
): number {
  const totalVolume = bidVolume + askVolume;
  if (totalVolume === 0) return 0;
  const weight = iv / totalVolume;
  if (optionType === "C") return (askVolume - bidVolume) * weight;
  return (bidVolume - askVolume) * weight;
}

/**
 * Calculate options-aware Mal value with Gamma
 * Mal = (Bid Volume - Ask Volume) * Gamma / (Bid Volume + Ask Volume)
 * For options: Adjust based on option type and sentiment
 */
export function calculateOptionsAwareMal(
  bidVolume: number,
  askVolume: number,
  optionType: "C" | "P",
  _delta: number,
  gamma: number = 0
): number {
  const totalVolume = bidVolume + askVolume;
  if (totalVolume === 0) return 0;
  const weight = gamma / totalVolume;
  if (optionType === "C") return (askVolume - bidVolume) * weight;
  return (bidVolume - askVolume) * weight;
}

/**
 * Calculate options-aware Band value
 * Band = (Bid Count - Ask Count) / (Bid Count + Ask Count)
 * For options: Adjust based on option type and trading activity
 */
export function calculateOptionsAwareBand(
  bidCount: number,
  askCount: number,
  optionType: "C" | "P",
  _delta: number
): number {
  const totalCount = bidCount + askCount;
  if (totalCount === 0) return 0;
  if (optionType === "C") return (askCount - bidCount) / totalCount;
  return (bidCount - askCount) / totalCount;
}

/**
 * Calculate options-aware Zahn value
 * Zahn = (Bid Volume accumulated - Ask Volume accumulated) / (Bid Volume accumulated + Ask Volume accumulated)
 * For options: Adjust based on option type and accumulated sentiment
 */
export function calculateOptionsAwareZahn(
  bidVolumeAcc: number,
  askVolumeAcc: number,
  optionType: "C" | "P",
  _deltaAcc: number
): number {
  const totalVolumeAcc = bidVolumeAcc + askVolumeAcc;
  if (totalVolumeAcc === 0) return 0;
  if (optionType === "C") return (askVolumeAcc - bidVolumeAcc) / totalVolumeAcc;
  return (bidVolumeAcc - askVolumeAcc) / totalVolumeAcc;
}

/**
 * Determine option strategy based on sentiment and option type
 */
export function getOptionStrategy(
  sentiment: string,
  optionType: 'C' | 'P',
  direction: number
): string {
  try {
    const isBullish = sentiment.toLowerCase().includes('bullish');
    const isBearish = sentiment.toLowerCase().includes('bearish');
    
    if (optionType === 'C') {
      if (isBullish) {
        return direction > 0 ? 'Long Call (Aggressive)' : 'Short Call (Protective)';
      } else if (isBearish) {
        return direction < 0 ? 'Short Call (Protective)' : 'Long Call (Aggressive)';
      }
    } else {
      if (isBullish) {
        return direction < 0 ? 'Long Put (Aggressive)' : 'Short Put (Protective)';
      } else if (isBearish) {
        return direction > 0 ? 'Short Put (Protective)' : 'Long Put (Aggressive)';
      }
    }
    
    return 'Neutral Strategy';
  } catch (error) {
    console.error('Error determining option strategy:', error);
    return 'Unknown Strategy';
  }
}

/**
 * Calculate underlying sentiment considering option type
 */
export function calculateUnderlyingSentiment(
  delta: number,
  optionType: 'C' | 'P',
  direction: number
): string {
  try {
    let underlyingSentiment: string;
    
    if (optionType === 'C') {
      // For calls: positive delta = bullish for underlying
      if (delta > 0) {
        underlyingSentiment = 'Bullish';
      } else if (delta < 0) {
        underlyingSentiment = 'Bearish';
      } else {
        underlyingSentiment = 'Neutral';
      }
    } else {
      // For puts: negative delta = bullish for underlying
      if (delta < 0) {
        underlyingSentiment = 'Bullish';
      } else if (delta > 0) {
        underlyingSentiment = 'Bearish';
      } else {
        underlyingSentiment = 'Neutral';
      }
    }
    
    return underlyingSentiment;
  } catch (error) {
    console.error('Error calculating underlying sentiment:', error);
    return 'Neutral';
  }
}

/**
 * Process scanner data to add options-specific fields
 */
function readNumericField(item: any, logicalField: string, dataMode: DataMode): number {
  const v = getFieldValue(item, logicalField, dataMode);
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function processOptionsData(data: any[], dataMode: DataMode = '5min'): OptionsScannerData[] {
  return data.map(item => {
    const optionInfo = extractOptionType(item.Symbol);
    let underlyingSymbol = '';
    let optionType: 'C' | 'P' = 'C'; // Default to call
    

    
    // First try to use the existing Underlying column if available.
    // Some runtime payloads can carry non-string values (number/object), so normalize safely.
    const rawUnderlying = item?.Underlying;
    const normalizedUnderlying =
      rawUnderlying === null || rawUnderlying === undefined
        ? ''
        : String(rawUnderlying).trim();
    if (normalizedUnderlying) {
      underlyingSymbol = normalizedUnderlying;
    } else if (optionInfo) {
      underlyingSymbol = optionInfo.symbol;
    } else {
      underlyingSymbol = getUnderlyingTicker(item.Symbol);
    }
    
    // Apply unified symbol mapping to the underlying symbol
    underlyingSymbol = getUnifiedMappedSymbol(underlyingSymbol);
    

    
    // First try to use the existing Option Type column if available
    if (item['Option Type'] && (item['Option Type'] === 'C' || item['Option Type'] === 'P')) {
      optionType = item['Option Type'];
    } else if (optionInfo) {
      optionType = optionInfo.type;
    } else {
      // Fallback: try to extract from symbol
      const symbolStr = String(item.Symbol || '');
      const symbolUpper = symbolStr.toUpperCase();
      if (symbolUpper.includes('C')) {
        optionType = 'C';
      } else if (symbolUpper.includes('P')) {
        optionType = 'P';
      }
    }

    const bidVolume = readNumericField(item, 'Bid Volume', dataMode);
    const askVolume = readNumericField(item, 'Ask Volume', dataMode);
    const bidCount = readNumericField(item, 'Bid Count', dataMode);
    const askCount = readNumericField(item, 'Ask Count', dataMode);
    const deltaFlow = readNumericField(item, 'Delta', dataMode);
    
    if (optionInfo) {
      const { sentiment, direction } = calculateOptionsSentiment(
        deltaFlow,
        optionInfo.type,
        bidVolume,
        askVolume
      );
      
      const optionsAwareHaltz = calculateOptionsAwareHaltz(
        bidVolume,
        askVolume,
        optionInfo.type,
        deltaFlow,
        item['IV'] || 0
      );
      
      const optionsAwareMal = calculateOptionsAwareMal(
        bidVolume,
        askVolume,
        optionInfo.type,
        deltaFlow,
        item['Gamma'] || 0
      );
      
      const optionsAwareBand = calculateOptionsAwareBand(
        bidCount,
        askCount,
        optionInfo.type,
        deltaFlow
      );
      
      const optionsAwareZahn = calculateOptionsAwareZahn(
        bidVolume,
        askVolume,
        optionInfo.type,
        deltaFlow
      );
      
      const underlyingSentiment = calculateUnderlyingSentiment(
        deltaFlow,
        optionInfo.type,
        deltaFlow
      );
      
      const optionStrategy = getOptionStrategy(
        sentiment,
        optionInfo.type,
        deltaFlow
      );
      
      // Calculate moneyness using the underlying price
      const underlyingPrice = getUnderlyingPrice(underlyingSymbol);
      const moneyness = classifyMoneyness(optionInfo.strike, underlyingPrice, optionInfo.type);
      const numericMoneyness = calculateNumericMoneyness(optionInfo.strike, underlyingPrice, optionInfo.type);
      
      // Preserve CBOE/data source Greeks when present; use placeholders only when missing
      const rawDelta = item['Delta'] ?? item['Delta accumelated'] ?? item['Delta ( ask - bid )'];
      const rawGamma = item['Gamma'] ?? item.gamma;
      const rawTheta = item['Theta'] ?? item.theta;
      const rawVega = item['Vega'] ?? item.vega;
      const delta = typeof rawDelta === 'number' && Number.isFinite(rawDelta) ? Number(rawDelta) : calculateDelta({ ...item, optionType: optionInfo.type, underlyingSymbol });
      const gamma = typeof rawGamma === 'number' && Number.isFinite(rawGamma) ? Number(rawGamma) : calculateGamma({ ...item, optionType: optionInfo.type, underlyingSymbol });
      const theta = typeof rawTheta === 'number' && Number.isFinite(rawTheta) ? Number(rawTheta) : calculateTheta({ ...item, optionType: optionInfo.type, underlyingSymbol });
      const vega = typeof rawVega === 'number' && Number.isFinite(rawVega) ? Number(rawVega) : calculateVega({ ...item, optionType: optionInfo.type, underlyingSymbol });

      return {
        ...item,
        optionType: optionInfo.type,
        underlyingSymbol: underlyingSymbol,
        properSentiment: sentiment,
        properDirection: direction,
        optionsAwareHaltz,
        optionsAwareBand,
        optionsAwareZahn,
        optionsAwareMal,
        underlyingSentiment,
        optionStrategy,
        moneyness,
        numericMoneyness,
        delta,
        gamma,
        theta,
        vega
      };
    } else {
      // Fallback processing when option info can't be extracted
      const { sentiment, direction } = calculateOptionsSentiment(
        deltaFlow,
        optionType,
        bidVolume,
        askVolume
      );
      
      const optionsAwareHaltz = calculateOptionsAwareHaltz(
        bidVolume,
        askVolume,
        optionType,
        deltaFlow,
        item['IV'] || 0 // Assuming IV is available in the data
      );
      
      const optionsAwareMal = calculateOptionsAwareMal(
        bidVolume,
        askVolume,
        optionType,
        deltaFlow,
        item['Gamma'] || 0 // Assuming Gamma is available in the data
      );
      
      const optionsAwareBand = calculateOptionsAwareBand(
        bidCount,
        askCount,
        optionType,
        deltaFlow
      );
      
      const optionsAwareZahn = calculateOptionsAwareZahn(
        bidVolume,
        askVolume,
        optionType,
        deltaFlow
      );
      
      const underlyingSentiment = calculateUnderlyingSentiment(
        deltaFlow,
        optionType,
        deltaFlow
      );
      
      const optionStrategy = getOptionStrategy(
        sentiment,
        optionType,
        deltaFlow
      );
      
      // Calculate moneyness using the underlying price
      const underlyingPrice = getUnderlyingPrice(underlyingSymbol);
      const moneyness = classifyMoneyness(item.Strike || 0, underlyingPrice, optionType);
      const numericMoneyness = calculateNumericMoneyness(item.Strike || 0, underlyingPrice, optionType);
      
      // Preserve CBOE/data source Greeks when present; use placeholders only when missing
      const rawDelta = item['Delta'] ?? item['Delta accumelated'] ?? item['Delta ( ask - bid )'];
      const rawGamma = item['Gamma'] ?? item.gamma;
      const rawTheta = item['Theta'] ?? item.theta;
      const rawVega = item['Vega'] ?? item.vega;
      const delta = typeof rawDelta === 'number' && Number.isFinite(rawDelta) ? Number(rawDelta) : calculateDelta({ ...item, optionType, underlyingSymbol });
      const gamma = typeof rawGamma === 'number' && Number.isFinite(rawGamma) ? Number(rawGamma) : calculateGamma({ ...item, optionType, underlyingSymbol });
      const theta = typeof rawTheta === 'number' && Number.isFinite(rawTheta) ? Number(rawTheta) : calculateTheta({ ...item, optionType, underlyingSymbol });
      const vega = typeof rawVega === 'number' && Number.isFinite(rawVega) ? Number(rawVega) : calculateVega({ ...item, optionType, underlyingSymbol });

      return {
        ...item,
        optionType,
        underlyingSymbol,
        properSentiment: sentiment,
        properDirection: direction,
        optionsAwareHaltz,
        optionsAwareBand,
        optionsAwareZahn,
        optionsAwareMal,
        underlyingSentiment,
        optionStrategy,
        moneyness,
        numericMoneyness,
        delta,
        gamma,
        theta,
        vega
      };
    }
  });
}

/**
 * Get field names based on data mode and option type
 */
export function getOptionsFieldNames(dataMode: '5min' | 'accumulated', optionType?: 'C' | 'P') {
  const baseFields = {
    bidCount: dataMode === '5min' ? 'Bid Count' : 'Bid Count accumelated',
    askCount: dataMode === '5min' ? 'Ask Count' : 'Ask Count accumelated',
    bidVolume: dataMode === '5min' ? 'Bid Volume' : 'Bid Volume accumelated',
    askVolume: dataMode === '5min' ? 'Ask Volume' : 'Ask Volume accumelated',
    delta: dataMode === '5min' ? 'Delta ( ask - bid )' : 'Delta accumelated',
    sentiment: dataMode === '5min' ? 'Sentiment' : 'Sentiment accumelated',
    direction: dataMode === '5min' ? 'Direction' : 'Direction accumelated'
  };
  
  return baseFields;
}

/**
 * Filter data by option type
 */
export function filterByOptionType(data: OptionsScannerData[], optionType?: 'C' | 'P' | 'ALL') {
  if (!optionType || optionType === 'ALL') {
    return data;
  }
  return data.filter(item => item.optionType === optionType);
}

/**
 * Get option type display name
 */
export function getOptionTypeDisplayName(type: 'C' | 'P'): string {
  return type === 'C' ? 'Calls' : 'Puts';
}

/**
 * Get sentiment color for options
 */
export function getOptionsSentimentColor(sentiment: string, optionType: 'C' | 'P'): string {
  switch (sentiment.toLowerCase()) {
    case 'very bullish':
    case 'bullish':
      return '#10b981'; // Green
    case 'very bearish':
    case 'bearish':
      return '#ef4444'; // Red
    case 'neutral':
    default:
      return '#6b7280'; // Gray
  }
}

/**
 * Get comprehensive options analysis summary
 */
export function getOptionsAnalysisSummary(data: OptionsScannerData[]) {
  const calls = data.filter(item => item.optionType === 'C');
  const puts = data.filter(item => item.optionType === 'P');
  
  const callSentiments = calls.reduce((acc, item) => {
    acc[item.properSentiment] = (acc[item.properSentiment] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const putSentiments = puts.reduce((acc, item) => {
    acc[item.properSentiment] = (acc[item.properSentiment] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const underlyingSentiments = data.reduce((acc, item) => {
    acc[item.underlyingSentiment] = (acc[item.underlyingSentiment] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  return {
    totalOptions: data.length,
    calls: {
      count: calls.length,
      sentiments: callSentiments,
      avgHaltz: calls.reduce((sum, item) => sum + item.optionsAwareHaltz, 0) / calls.length || 0,
      avgBand: calls.reduce((sum, item) => sum + item.optionsAwareBand, 0) / calls.length || 0,
      avgZahn: calls.reduce((sum, item) => sum + item.optionsAwareZahn, 0) / calls.length || 0
    },
    puts: {
      count: puts.length,
      sentiments: putSentiments,
      avgHaltz: puts.reduce((sum, item) => sum + item.optionsAwareHaltz, 0) / puts.length || 0,
      avgBand: puts.reduce((sum, item) => sum + item.optionsAwareBand, 0) / puts.length || 0,
      avgZahn: puts.reduce((sum, item) => sum + item.optionsAwareZahn, 0) / puts.length || 0
    },
    underlyingSentiments,
    strategies: data.reduce((acc, item) => {
      acc[item.optionStrategy] = (acc[item.optionStrategy] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  };
}

/**
 * Aggregate option sentiment across all options
 * Returns counts for bullish, bearish, defensive bullish, and defensive bearish
 */
export function aggregateOptionSentiment(options: OptionsScannerData[], dataMode: DataMode = '5min'): {
  bullish: number;
  bearish: number;
  defensiveBullish: number;
  defensiveBearish: number;
  net: number;
} {
  let bullish = 0;
  let bearish = 0;
  let defensiveBullish = 0;
  let defensiveBearish = 0;

  for (const opt of options) {
    const bought = readNumericField(opt as any, 'Ask Volume', dataMode);
    const sold = readNumericField(opt as any, 'Bid Volume', dataMode);
    
    if (opt.optionType === 'C') {
      // For calls:
      // - Bought calls = bullish (expecting price to go up)
      // - Sold calls = defensive bearish (expecting price to stay down or go down)
      bullish += bought;        // Bought calls = bullish
      defensiveBearish += sold; // Sold calls = defensive bearish
    } else if (opt.optionType === 'P') {
      // For puts:
      // - Bought puts = bearish (expecting price to go down)
      // - Sold puts = defensive bullish (expecting price to stay up or go up)
      bearish += bought;        // Bought puts = bearish
      defensiveBullish += sold; // Sold puts = defensive bullish
    }
  }
  
  // Net: bullish + defensive bullish - bearish - defensive bearish
  const net = bullish + defensiveBullish - bearish - defensiveBearish;
  
  return { bullish, bearish, defensiveBullish, defensiveBearish, net };
}

/**
 * Extract underlying ticker from option symbol
 * Example: "NVDA 20250813C100" -> "NVDA"
 */
export function getUnderlyingTicker(symbol: any): string {
  // Handle non-string inputs safely
  if (!symbol || typeof symbol !== 'string') {
    console.warn('getUnderlyingTicker: Invalid symbol input:', symbol);
    return '';
  }
  
  // Extract underlying ticker from option symbol (e.g., NVDA 20250813C100 -> NVDA)
  const match = symbol.match(/^([A-Z]+)/);
  return match ? match[1] : symbol;
}

/**
 * Get underlying price for a symbol (placeholder implementation)
 * In a real implementation, this would fetch from a price API
 */
export function getUnderlyingPrice(symbol: string): number {
  // Placeholder implementation - in real app would fetch from price API
  // For now, return a default value based on common stock prices
  const defaultPrices: Record<string, number> = {
    'NVDA': 120,
    'AAPL': 180,
    'TSLA': 250,
    'SPY': 628.77,  // Current SPY price
    'QQQ': 564.17,  // Current QQQ price
    'IWM': 190,
    'SPX': 6305.6,  // Current S&P 500 price
    'NDX': 20974.174,  // Current NASDAQ price
    'GOLD': 3410.3,  // Current Gold price
    'GLD': 313.13,   // Current GLD price
    'SP500': 6305.6, // Current S&P 500 price
    'NASDAQ': 20974.174 // Current NASDAQ price
  };
  
  return defaultPrices[symbol] || 100; // Default to $100 if not found
}

// Advanced analysis functions
export const classifyMoneyness = (
  strike: number, 
  underlyingPrice: number, 
  optionType: 'C' | 'P',
  otmThreshold: number = 0.3,
  atmThreshold: number = 0.7
): 'OTM' | 'ATM' | 'ITM' | 'Unknown' => {
  try {
    if (!strike || !underlyingPrice || underlyingPrice <= 0) {
      return 'Unknown';
    }
    
    const ratio = strike / underlyingPrice;
    
    if (optionType === 'C') {
      // For calls: ITM when strike < underlying, OTM when strike > underlying
      if (ratio < otmThreshold) return 'ITM';
      if (ratio > atmThreshold) return 'OTM';
      return 'ATM';
    } else {
      // For puts: ITM when strike > underlying, OTM when strike < underlying
      if (ratio > atmThreshold) return 'ITM';
      if (ratio < otmThreshold) return 'OTM';
      return 'ATM';
    }
  } catch (error) {
    console.error('Error classifying moneyness:', error);
    return 'Unknown';
  }
};

export const assignScenario = (params: {
  optionType: 'C' | 'P';
  moneyness: 'OTM' | 'ATM' | 'ITM' | 'Unknown';
  delta: number;
  volume: number;
  beltStrength: string;
}): string => {
  const { optionType, moneyness, delta, volume, beltStrength } = params;
  
  // High volume scenarios
  if (volume > 1000) {
    if (optionType === 'C' && delta > 0.5) {
      return 'Bullish Momentum';
    } else if (optionType === 'P' && delta < -0.5) {
      return 'Bullish Momentum';
    } else if (optionType === 'C' && delta < 0.3) {
      return 'Bearish Pressure';
    } else if (optionType === 'P' && delta > -0.3) {
      return 'Bearish Pressure';
    }
  }
  
  // Moneyness-based scenarios
  if (moneyness === 'OTM') {
    if (beltStrength === 'Strong') {
      return 'Volatility Expansion';
    } else {
      return 'Neutral Range';
    }
  } else if (moneyness === 'ITM') {
    if (Math.abs(delta) > 0.7) {
      return optionType === 'C' ? 'Bullish Momentum' : 'Bearish Pressure';
    } else {
      return 'Consolidation';
    }
  } else {
    // ATM
    if (beltStrength === 'Strong') {
      return 'Volatility Expansion';
    } else {
      return 'Neutral Range';
    }
  }
};

// Enhanced moneyness classification with dynamic thresholds
export const classifyMoneynessWithThresholds = (
  strike: number,
  underlyingPrice: number,
  optionType: 'C' | 'P',
  otmThreshold: number,
  atmThreshold: number
): 'OTM' | 'ATM' | 'ITM' | 'Unknown' => {
  return classifyMoneyness(strike, underlyingPrice, optionType, otmThreshold, atmThreshold);
};

// New numeric moneyness system (-50 to 50)
export const calculateNumericMoneyness = (
  strike: number,
  underlyingPrice: number,
  optionType: 'C' | 'P'
): number => {
  if (!underlyingPrice || underlyingPrice <= 0) return 0;
  
  const moneynessRatio = strike / underlyingPrice;
  
  if (optionType === 'C') {
    // For calls: 0 = ATM, negative = ITM, positive = OTM
    // Convert to -50 to 50 scale
    if (moneynessRatio <= 1) {
      // ITM or ATM: 0 to -50
      return Math.max(-50, (moneynessRatio - 1) * 100);
    } else {
      // OTM: 0 to 50
      return Math.min(50, (moneynessRatio - 1) * 100);
    }
  } else {
    // For puts: 0 = ATM, positive = ITM, negative = OTM
    // Convert to -50 to 50 scale
    if (moneynessRatio >= 1) {
      // ITM or ATM: 0 to 50
      return Math.min(50, (moneynessRatio - 1) * 100);
    } else {
      // OTM: 0 to -50
      return Math.max(-50, (moneynessRatio - 1) * 100);
    }
  }
};

// Calculate moneyness percentage for filtering (divide by 100)
export const calculateMoneynessPercentage = (
  strike: number,
  underlyingPrice: number,
  optionType: 'C' | 'P'
): number => {
  const numericMoneyness = calculateNumericMoneyness(strike, underlyingPrice, optionType);
  return numericMoneyness / 100; // Convert to percentage
};

// Classify moneyness based on percentage with 10% threshold for ATM
export const classifyMoneynessByPercentage = (
  moneynessPercentage: number,
  atmThreshold: number = 0.10 // 10% threshold
): 'OTM' | 'ATM' | 'ITM' => {
  if (Math.abs(moneynessPercentage) <= atmThreshold) {
    return 'ATM';
  } else if (moneynessPercentage > atmThreshold) {
    return 'OTM';
  } else {
    return 'ITM';
  }
};

// Filter by numeric moneyness with margin
export const filterByNumericMoneyness = (
  data: OptionsScannerData[],
  margin: number = 10,
  moneynessType: 'ALL' | 'CALLS' | 'PUTS' = 'ALL'
): OptionsScannerData[] => {
  return data.filter(item => {
    const underlyingPrice = getUnderlyingPrice(item.underlyingSymbol);
    const numericMoneyness = calculateNumericMoneyness(
      item.Strike || 0,
      underlyingPrice,
      item.optionType
    );
    
    // Check if within margin range (-margin to +margin)
    const withinMargin = Math.abs(numericMoneyness) <= margin;
    
    // Check option type filter
    const typeMatch = moneynessType === 'ALL' || 
                     (moneynessType === 'CALLS' && item.optionType === 'C') ||
                     (moneynessType === 'PUTS' && item.optionType === 'P');
    
    return withinMargin && typeMatch;
  });
};

// Filter by moneyness percentage with 10% threshold for ATM
export const filterByMoneynessPercentage = (
  data: OptionsScannerData[],
  selectedMoneyness: 'OTM' | 'ATM' | 'ITM' | 'ALL',
  atmThreshold: number = 0.10 // 10% threshold
): OptionsScannerData[] => {
  if (selectedMoneyness === 'ALL') {
    return data;
  }
  
  return data.filter(item => {
    const moneynessPercentage = item.numericMoneyness / 100; // Convert to percentage
    const classifiedMoneyness = classifyMoneynessByPercentage(moneynessPercentage, atmThreshold);
    return classifiedMoneyness === selectedMoneyness;
  });
};

/**
 * Unified function to calculate options-aware sentiment and direction
 * This function should be used consistently across all charts and analysis components
 * 
 * @param delta - The delta value (bid volume - ask volume)
 * @param optionType - 'C' for call, 'P' for put
 * @param bidVolume - Bid volume
 * @param askVolume - Ask volume
 * @param direction - Raw direction value (optional)
 * @returns Object with sentiment, direction, and strength
 */
export function getOptionsAwareSentiment(
  delta: number,
  optionType: 'C' | 'P',
  bidVolume: number,
  askVolume: number,
  direction?: number
): { sentiment: string; direction: string; strength: number; color: string } {
  try {
    let sentiment: string;
    let directionStr: string;
    let strength: number;
    let color: string;
    
    const totalVolume = bidVolume + askVolume;
    
    // CORRECT: Use bid/ask volume ratio for sentiment calculation
    // bidVolume = selling, askVolume = buying
    const volumeRatio = totalVolume > 0 ? (askVolume - bidVolume) / totalVolume : 0;
    
    // Core options-aware logic based on bid/ask volumes:
    if (optionType === 'C') {
      // For calls: bought calls (ask > bid) = bullish, sold calls (bid > ask) = bearish
      if (volumeRatio > 0.1) {
        sentiment = 'Bullish';
        directionStr = 'Up';
        strength = Math.abs(volumeRatio);
        color = '#10b981'; // green
      } else if (volumeRatio < -0.1) {
        sentiment = 'Bearish';
        directionStr = 'Down';
        strength = Math.abs(volumeRatio);
        color = '#ef4444'; // red
      } else {
        sentiment = 'Neutral';
        directionStr = 'Sideways';
        strength = 0;
        color = '#6b7280'; // gray
      }
    } else { // optionType === 'P'
      // For puts: bought puts (ask > bid) = bearish, sold puts (bid > ask) = bullish
      if (volumeRatio > 0.1) {
        sentiment = 'Bearish';
        directionStr = 'Down';
        strength = Math.abs(volumeRatio);
        color = '#ef4444'; // red
      } else if (volumeRatio < -0.1) {
        sentiment = 'Bullish';
        directionStr = 'Up';
        strength = Math.abs(volumeRatio);
        color = '#10b981'; // green
      } else {
        sentiment = 'Neutral';
        directionStr = 'Sideways';
        strength = 0;
        color = '#6b7280'; // gray
      }
    }
    
    // Override with provided direction if available
    if (direction !== undefined) {
      if (direction > 0) {
        directionStr = 'Up';
      } else if (direction < 0) {
        directionStr = 'Down';
      } else {
        directionStr = 'Sideways';
      }
    }
    
    return { sentiment, direction: directionStr, strength, color };
  } catch (error) {
    console.error('Error calculating options-aware sentiment:', error);
    return { 
      sentiment: 'Neutral', 
      direction: 'Sideways', 
      strength: 0, 
      color: '#6b7280' 
    };
  }
}

/**
 * Get sentiment color based on sentiment and option type
 * @param sentiment - The sentiment string
 * @param optionType - 'C' for call, 'P' for put
 * @returns CSS color string
 */
export function getSentimentColor(sentiment: string, optionType: 'C' | 'P'): string {
  const sentimentLower = sentiment.toLowerCase();
  
  if (sentimentLower.includes('bullish') || sentimentLower.includes('bull')) {
    return '#10b981'; // green-500
  } else if (sentimentLower.includes('bearish') || sentimentLower.includes('bear')) {
    return '#ef4444'; // red-500
  } else if (sentimentLower.includes('neutral')) {
    return '#6b7280'; // gray-500
  } else if (sentimentLower.includes('defensive')) {
    return optionType === 'C' ? '#f59e0b' : '#8b5cf6'; // amber-500 for calls, violet-500 for puts
  }
  
  return '#6b7280'; // default gray
}

/**
 * Calculate fear/greed sentiment based on option volume patterns
 */
export function calculateFearGreedSentiment(
  boughtCalls: number,
  soldCalls: number,
  boughtPuts: number,
  soldPuts: number,
  totalVolume: number
): { fearIndex: number; greedIndex: number; sentiment: string; color: string } {
  if (totalVolume === 0) {
    return { fearIndex: 0.5, greedIndex: 0.5, sentiment: 'Neutral', color: '#6b7280' };
  }
  
  // Fear indicators: bought puts (bearish) + sold calls (bearish)
  const fearVolume = boughtPuts + soldCalls;
  
  // Greed indicators: bought calls (bullish) + sold puts (bullish)
  const greedVolume = boughtCalls + soldPuts;
  
  const fearRatio = fearVolume / totalVolume;
  const greedRatio = greedVolume / totalVolume;
  
  const fearIndex = Math.min(fearRatio, 1);
  const greedIndex = Math.min(greedRatio, 1);
  
  let sentiment: string;
  let color: string;
  
  if (fearIndex > 0.6) {
    sentiment = 'Fear';
    color = '#ef4444'; // red
  } else if (greedIndex > 0.6) {
    sentiment = 'Greed';
    color = '#10b981'; // green
  } else if (fearIndex > 0.4) {
    sentiment = 'Mild Fear';
    color = '#f59e0b'; // amber
  } else if (greedIndex > 0.4) {
    sentiment = 'Mild Greed';
    color = '#84cc16'; // lime
  } else {
    sentiment = 'Neutral';
    color = '#6b7280'; // gray
  }
  
  return { fearIndex, greedIndex, sentiment, color };
}

/**
 * Calculate volume pressure and flow analysis
 */
export function calculateVolumePressureFlow(
  boughtCalls: number,
  soldCalls: number,
  boughtPuts: number,
  soldPuts: number,
  delta: number,
  totalVolume: number
): {
  volumePressure: number;
  directionalPressure: number;
  callFlow: number;
  putFlow: number;
  overallFlow: number;
  pressureLevel: string;
  flowDirection: string;
} {
  // Volume pressure based on volume imbalance
  const netVolume = (boughtCalls + boughtPuts) - (soldCalls + soldPuts);
  const volumePressure = totalVolume > 0 ? Math.abs(netVolume) / totalVolume : 0;
  
  // Directional pressure based on delta and volume
  const directionalPressure = Math.abs(delta) * totalVolume;
  
  // Flow calculations
  const callFlow = boughtCalls - soldCalls;
  const putFlow = soldPuts - boughtPuts; // Corrected: sold puts bullish, bought puts bearish
  const overallFlow = callFlow + putFlow;
  
  // Pressure level classification
  let pressureLevel: string;
  if (volumePressure > 0.7) {
    pressureLevel = 'Extreme';
  } else if (volumePressure > 0.5) {
    pressureLevel = 'High';
  } else if (volumePressure > 0.3) {
    pressureLevel = 'Moderate';
  } else if (volumePressure > 0.1) {
    pressureLevel = 'Low';
  } else {
    pressureLevel = 'Minimal';
  }
  
  // Flow direction classification
  let flowDirection: string;
  if (overallFlow > 0) {
    flowDirection = 'Bullish';
  } else if (overallFlow < 0) {
    flowDirection = 'Bearish';
  } else {
    flowDirection = 'Neutral';
  }
  
  return {
    volumePressure,
    directionalPressure,
    callFlow,
    putFlow,
    overallFlow,
    pressureLevel,
    flowDirection
  };
}

/**
 * Calculate comprehensive option sentiment analysis
 */
export function calculateComprehensiveOptionSentiment(
  boughtCalls: number,
  soldCalls: number,
  boughtPuts: number,
  soldPuts: number,
  delta: number,
  totalVolume: number,
  optionType: 'C' | 'P'
): {
  sentiment: string;
  fearGreed: { fearIndex: number; greedIndex: number; sentiment: string; color: string };
  pressureFlow: {
    volumePressure: number;
    directionalPressure: number;
    callFlow: number;
    putFlow: number;
    overallFlow: number;
    pressureLevel: string;
    flowDirection: string;
  };
  overallSentiment: string;
  overallColor: string;
} {
  const fearGreed = calculateFearGreedSentiment(boughtCalls, soldCalls, boughtPuts, soldPuts, totalVolume);
  const pressureFlow = calculateVolumePressureFlow(boughtCalls, soldCalls, boughtPuts, soldPuts, delta, totalVolume);
  
  // Determine overall sentiment based on multiple factors
  let overallSentiment: string;
  let overallColor: string;
  
  const bullishFactors = [
    pressureFlow.flowDirection === 'Bullish' ? 1 : 0,
    fearGreed.sentiment === 'Greed' || fearGreed.sentiment === 'Mild Greed' ? 1 : 0,
    pressureFlow.overallFlow > 0 ? 1 : 0
  ];
  
  const bearishFactors = [
    pressureFlow.flowDirection === 'Bearish' ? 1 : 0,
    fearGreed.sentiment === 'Fear' || fearGreed.sentiment === 'Mild Fear' ? 1 : 0,
    pressureFlow.overallFlow < 0 ? 1 : 0
  ];
  
  const bullishScore = bullishFactors.reduce((sum, factor) => sum + factor, 0);
  const bearishScore = bearishFactors.reduce((sum, factor) => sum + factor, 0);
  
  if (bullishScore > bearishScore && bullishScore >= 2) {
    overallSentiment = 'Strong Bullish';
    overallColor = '#10b981'; // green
  } else if (bearishScore > bullishScore && bearishScore >= 2) {
    overallSentiment = 'Strong Bearish';
    overallColor = '#ef4444'; // red
  } else if (bullishScore > bearishScore) {
    overallSentiment = 'Bullish';
    overallColor = '#84cc16'; // lime
  } else if (bearishScore > bullishScore) {
    overallSentiment = 'Bearish';
    overallColor = '#f59e0b'; // amber
  } else {
    overallSentiment = 'Neutral';
    overallColor = '#6b7280'; // gray
  }
  
  return {
    sentiment: overallSentiment,
    fearGreed,
    pressureFlow,
    overallSentiment,
    overallColor
  };
}

/**
 * Safely parse direction values to prevent NaN issues
 * @param directionValue - The direction value to parse
 * @returns A valid number (0 if parsing fails)
 */
export function safeParseDirection(directionValue: any): number {
  try {
    if (directionValue === null || directionValue === undefined) {
      return 0;
    }
    
    if (typeof directionValue === 'number') {
      return isNaN(directionValue) ? 0 : directionValue;
    }
    
    if (typeof directionValue === 'string') {
      const trimmed = directionValue.trim();
      if (trimmed === '') {
        return 0;
      }
      const parsed = parseFloat(trimmed);
      return isNaN(parsed) ? 0 : parsed;
    }
    
    return 0;
  } catch (error) {
    console.warn('Error parsing direction value:', directionValue, error);
    return 0;
  }
}

// --- Greeks Calculation Placeholders ---
export function calculateDelta(option: OptionsScannerData): number {
  // Placeholder: real calculation needs more data (IV, time, etc.)
  return 0.5; // Dummy value
}
export function calculateGamma(option: OptionsScannerData): number {
  return 0.1;
}
export function calculateTheta(option: OptionsScannerData): number {
  return -0.02;
}
export function calculateVega(option: OptionsScannerData): number {
  return 0.15;
} 

/**
 * Symbol mapping configuration for common ETF/Index pairs
 */
export const SYMBOL_MAPPINGS: Record<string, string> = {
  'GOLD': 'GLD',
  'GLD': 'GOLD',
  'QQQ': 'NASDAQ',
  'NASDAQ': 'QQQ',
  'SPY': 'SP500',
  'SP500': 'SPY',
  'NDX': 'NASDAQ',
  'SPXW': 'SPX',
  'SPX': 'SPXW'
};

/**
 * Price conversion ratios for symbol mappings
 * These ratios convert prices from one symbol to its related symbol
 */
export const PRICE_CONVERSION_RATIOS: Record<string, number> = {
  // GLD to GOLD conversion (GLD price * ratio = GOLD price)
  'GLD_TO_GOLD': 10.891, // Current ratio: GOLD price / GLD price
  'GOLD_TO_GLD': 0.0918, // Current ratio: GLD price / GOLD price
  
  // QQQ to NASDAQ conversion (QQQ price * ratio = NASDAQ price)
  'QQQ_TO_NASDAQ': 37.177, // Current ratio: NASDAQ price / QQQ price
  'NASDAQ_TO_QQQ': 0.0269, // Current ratio: QQQ price / NASDAQ price
  
  // SPY to SP500 conversion (SPY price * ratio = SP500 price)
  'SPY_TO_SP500': 10.028, // Current ratio: SP500 price / SPY price
  'SP500_TO_SPY': 0.0997, // Current ratio: SPY price / SP500 price
};

/**
 * Get the conversion ratio for mapping from source symbol to target symbol
 */
export function getConversionRatio(sourceSymbol: string, targetSymbol: string): number {
  const source = sourceSymbol.toUpperCase();
  const target = targetSymbol.toUpperCase();
  
  // GLD ↔ GOLD conversions
  if (source === 'GLD' && target === 'GOLD') return PRICE_CONVERSION_RATIOS.GLD_TO_GOLD;
  if (source === 'GOLD' && target === 'GLD') return PRICE_CONVERSION_RATIOS.GOLD_TO_GLD;
  
  // QQQ ↔ NASDAQ conversions
  if (source === 'QQQ' && target === 'NASDAQ') return PRICE_CONVERSION_RATIOS.QQQ_TO_NASDAQ;
  if (source === 'NASDAQ' && target === 'QQQ') return PRICE_CONVERSION_RATIOS.NASDAQ_TO_QQQ;
  
  // SPY ↔ SP500 conversions
  if (source === 'SPY' && target === 'SP500') return PRICE_CONVERSION_RATIOS.SPY_TO_SP500;
  if (source === 'SP500' && target === 'SPY') return PRICE_CONVERSION_RATIOS.SP500_TO_SPY;
  
  // No conversion needed
  return 1;
}

/**
 * Convert a price from one symbol to its related symbol
 */
export function convertPrice(price: number, sourceSymbol: string, targetSymbol: string): number {
  if (sourceSymbol.toUpperCase() === targetSymbol.toUpperCase()) return price;
  
  const ratio = getConversionRatio(sourceSymbol, targetSymbol);
  return price * ratio;
}

/**
 * Apply global symbol mapping to all price-related fields in a data item
 */
export function applyGlobalSymbolMapping(item: any, isEnabled: boolean = true): any {
  if (!isEnabled) return item;
  
  // Import the unified symbol mapping function
  const unifiedMapping = require('./unifiedSymbolMapping');
  
  // Use the unified symbol mapping system
  return unifiedMapping.applyUnifiedSymbolMapping(item);
}

/**
 * Apply global symbol mapping to an array of data items
 */
export function applyGlobalSymbolMappingToArray(data: any[], isEnabled: boolean = true): any[] {
  if (!isEnabled) return data;
  return data.map(item => applyGlobalSymbolMapping(item, isEnabled));
}

/**
 * Get the mapped symbol for a given symbol
 */
export function getMappedSymbol(symbol: string, isEnabled: boolean = true): string {
  const s = String(symbol ?? '').trim();
  if (!isEnabled) return s;
  
  // Import the unified symbol mapping function
  const unifiedMapping = require('./unifiedSymbolMapping');
  
  // Use the unified symbol mapping system
  return unifiedMapping.getMappedSymbol(s);
}

/**
 * Get all related symbols for a given symbol
 */
export function getRelatedSymbols(symbol: string, isEnabled: boolean = true): string[] {
  const base = String(symbol ?? '').trim();
  if (!isEnabled) return base ? [base.toUpperCase()] : [];
  
  const upperSymbol = base.toUpperCase();
  if (!upperSymbol) return [];
  const mapped = SYMBOL_MAPPINGS[upperSymbol];
  const reverseMapped = Object.entries(SYMBOL_MAPPINGS).find(([key, value]) => value === upperSymbol)?.[0];
  
  const related = [upperSymbol];
  if (mapped) related.push(mapped);
  if (reverseMapped) related.push(reverseMapped);
  
  return Array.from(new Set(related));
}

/**
 * Check if a symbol has a mapping
 */
export function hasSymbolMapping(symbol: string, isEnabled: boolean = true): boolean {
  if (!isEnabled) return false;
  const u = String(symbol ?? '').trim().toUpperCase();
  if (!u) return false;
  return u in SYMBOL_MAPPINGS || Object.values(SYMBOL_MAPPINGS).includes(u);
} 
